/**
 * Firebase configuration, and the one switch that decides whether this browser
 * talks to the emulators or to a real Firebase project.
 *
 * The switch is an explicit env flag rather than an inference from
 * `import.meta.env.DEV`. A developer running `vite preview` against production
 * data, or pointing a local build at staging, is a normal thing to want; tying
 * the decision to the build mode would make it impossible to say which one you
 * are getting without reading the bundler's mind.
 */

import type { FirebaseApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

export const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true";

const emulatorHost = import.meta.env.VITE_EMULATOR_HOST ?? "127.0.0.1";
const port = (value: string | undefined, fallback: number): number =>
    value ? Number.parseInt(value, 10) : fallback;

export const emulatorPorts = {
    auth: port(import.meta.env.VITE_EMULATOR_AUTH_PORT, 9099),
    firestore: port(import.meta.env.VITE_EMULATOR_FIRESTORE_PORT, 8080),
    storage: port(import.meta.env.VITE_EMULATOR_STORAGE_PORT, 9199),
    /* The admin role screen calls `setUserRole`, so the CMS needs the functions
       emulator too. Defaulted rather than required, because .env.example predates
       the functions emulator existing and an out-of-date .env should not break
       login. Must match the `functions` port in firebase.json. */
    functions: port(import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT, 5001)
};

export const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID
};

/** The public origin the guides are served from, used for internal link checks. */
export const siteOrigin = import.meta.env.VITE_SITE_ORIGIN ?? "https://zennopay.com";

/**
 * Points the SDK at the local emulator suite.
 *
 * Called from `useInitialiseFirebase`'s `onFirebaseInit`, which is the only
 * moment the app exists but no request has been issued yet. Each `connect*`
 * call throws if the corresponding service has already been used, so this must
 * not be deferred.
 */
export function connectEmulators(firebaseApp: FirebaseApp): void {
    connectAuthEmulator(
        getAuth(firebaseApp),
        `http://${emulatorHost}:${emulatorPorts.auth}`,
        { disableWarnings: true }
    );
    connectFirestoreEmulator(getFirestore(firebaseApp), emulatorHost, emulatorPorts.firestore);
    connectStorageEmulator(getStorage(firebaseApp), emulatorHost, emulatorPorts.storage);
    connectFunctionsEmulator(getFunctions(firebaseApp), emulatorHost, emulatorPorts.functions);
}

/** Human-readable reason the config is unusable, or `undefined` if it is fine. */
export function configError(): string | undefined {
    if (!firebaseConfig.projectId) {
        return "VITE_FIREBASE_PROJECT_ID is not set. Copy .env.example to .env at the repo root.";
    }
    if (!firebaseConfig.apiKey) {
        return "VITE_FIREBASE_API_KEY is not set. Copy .env.example to .env at the repo root.";
    }
    return undefined;
}
